import React from 'react';

import _ from 'lodash';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { MODALS, ANALYTICS_URL } from 'global-constants';

import WithUser from 'shared/HOCs/WithUser';
import WithPush from 'shared/HOCs/WithPush';

import Button from 'shared/components/Button';
import Loader from 'shared/components/Loader';
import ToggleBox from 'shared/components/ToggleBox';
import { openModal } from 'shared/components/Modals/reducer';

import {
  fetchRatings,
  updateRating,
  linkAnonymousRatingsToUser
} from './ducks';
import MaturityMatrixExplanationModal from './MaturityMatrixExplanationModal';
import {
  getUserRating,
  getAverageRating,
  limitPercentageValue,
  removeUserRatingFromRatings
} from './utils';

import styles from './styles.module.scss';

// INNOVATION = X
// ADAPTATION = Y

const InvestorStaticTextBoxes = () => (
  <React.Fragment>
    <div className={styles.investorLeftLabel}>Speed of Communications</div>
    <div className={styles.investorBottomLabel}>Fairness of Feedback</div>
    <div className={styles.investorTopLeftLabel}>Very Fast</div>
    <div className={styles.investorBottomRightLabel}>Very Fair</div>
  </React.Fragment>
);

const VendorStaticTextBoxes = ({ userRating }) => (
  <React.Fragment>
    <div className={styles.vendorLeftLabel}>Adoption</div>
    <div className={styles.vendorBottomLabel}>Innovation</div>
    <div className={styles.vendorTopLeftLabel}>Well Adopted</div>
    <div className={styles.vendorBottomRightLabel}>Highly Innovative</div>
    {userRating && (
      <React.Fragment>
        <div className={styles.established}>Established</div>
        <div className={styles.star}>Star</div>
        <div className={styles.agile}>Agile</div>
        <div className={styles.growth}>Growth</div>
      </React.Fragment>
    )}
  </React.Fragment>
);

class MaturityMatrix extends React.Component {
  state = {
    userRating: null,
    showPointPrompt: true,
    showAverage: false,
    averageRating: null,
    cursorFollowerRating: null,
    isOpenExplanationModal: false
  };

  componentDidMount() {
    this.fetchRatings();

    this.linkAnonymousRatingsToUser();
  }

  componentDidUpdate(prevProps) {
    if (_.isNil(prevProps.user) && this.props.user) {
      this.fetchRatings();
      this.linkAnonymousRatingsToUser();
    }
  }

  fetchRatings = () => {
    const { fetchRatings, type } = this.props;
    let { companySlug } = this.props.match.params;

    if (_.isNil(companySlug)) {
      companySlug = this.props.companySlug;
    }

    fetchRatings({
      type,
      slug: companySlug,
      extra: [
        this.updateAverageRating,
        this.getUserRatingFromPropsOrLocalStorage
      ]
    });
  };

  xToCoords = percentage => {
    return `calc(${percentage}% - 7.5px)`;
  };

  yToCoords = percentage => {
    return `calc(${percentage}% - 7.5px)`;
  };

  handleClick = e => {
    const {
      left,
      bottom,
      width,
      height
    } = this.ratingsContainer.getBoundingClientRect();

    const adaptation = limitPercentageValue(
      parseInt(((bottom - e.clientY) / height) * 100)
    );
    const innovation = limitPercentageValue(
      parseInt(((e.clientX - left) / width) * 100)
    );

    this.setState(
      { userRating: { adaptation, innovation } },
      this.updateRating
    );
  };

  handleMouseMove = e => {
    const {
      left,
      bottom,
      width,
      height
    } = this.ratingsContainer.getBoundingClientRect();

    const cursorFollowerRatingY = limitPercentageValue(
      parseInt(((bottom - e.clientY) / height) * 100)
    );
    const cursorFollowerRatingX = limitPercentageValue(
      parseInt(((e.clientX - left) / width) * 100)
    );

    this.setState({
      cursorFollowerRating: {
        innovation: cursorFollowerRatingX,
        adaptation: cursorFollowerRatingY
      }
    });
  };

  updateRating = () => {
    const { userRating } = this.state;
    const { type, updateRating } = this.props;
    let { companySlug } = this.props.match.params;

    if (_.isNil(companySlug)) {
      companySlug = this.props.companySlug;
    }

    const token = this.getPointTokenFromLocalStorage();

    this.updateAverageRating();

    updateRating({
      type,
      token,
      slug: companySlug,
      rating: userRating,
      savePointToLocalStorage: this.savePointToLocalStorage
    });
  };

  getPointTokenFromLocalStorage = () => {
    if (!_.isNil(this.props.user)) {
      return;
    }

    const { companySlug } = this.props.match.params;

    const votes = this.getLocalStorageVotes();

    const voteForCurrentCompany = _.head(
      _.filter(votes, vote => vote.companySlug === companySlug)
    );

    return _.get(voteForCurrentCompany, 'token');
  };

  savePointToLocalStorage = ({ token }) => {
    const { user } = this.props;
    const { userRating } = this.state;
    const { companySlug } = this.props.match.params;

    if (!user) {
      const previousVotes = this.getLocalStorageVotes();

      const previousVotesWithoutCurrentCompany = _.filter(
        previousVotes,
        vote => vote.companySlug !== companySlug
      );

      const newVote = { token, userRating, companySlug };

      const maturityMatrixVotes = [
        ...previousVotesWithoutCurrentCompany,
        newVote
      ];

      localStorage.setItem(
        'maturityMatrixVotes',
        JSON.stringify(maturityMatrixVotes)
      );
    }
  };

  getLocalStorageVotes = () =>
    JSON.parse(localStorage.getItem('maturityMatrixVotes'));

  clearLocalStorageVotes = () => localStorage.removeItem('maturityMatrixVotes');

  getUserRatingFromLocalStorage = () => {
    const { companySlug } = this.props.match.params;

    const votes = this.getLocalStorageVotes();

    const voteForCurrentCompany = _.head(
      _.filter(votes, vote => vote.companySlug === companySlug)
    );

    return _.get(voteForCurrentCompany, 'userRating');
  };

  linkAnonymousRatingsToUser = () => {
    const { user, linkAnonymousRatingsToUser } = this.props;

    const previousVotes = this.getLocalStorageVotes();

    if (!_.isNil(user) && !_.isEmpty(previousVotes)) {
      const previousVotesData = _.map(previousVotes, vote => ({
        token: vote.token
      }));

      linkAnonymousRatingsToUser({
        votesData: previousVotesData,
        extra: [this.clearLocalStorageVotes, this.fetchRatings]
      });
    }
  };

  updateAverageRating = fetchedRatings => {
    const { user } = this.props;
    let { userRating } = this.state;

    let ratings = fetchedRatings;

    if (_.isEmpty(ratings)) {
      ratings = this.props.ratings;
    }

    if (!userRating) {
      userRating = getUserRating({ ratings, user });

      if (!userRating) {
        return;
      }
    }

    const filteredRatings = removeUserRatingFromRatings({ ratings, user });

    const averageRating = getAverageRating({
      ratings: [...filteredRatings, userRating]
    });

    this.setState({ userRating, averageRating });
  };

  toggleExplanationModal = () =>
    this.setState(({ isOpenExplanationModal }) => ({
      isOpenExplanationModal: !isOpenExplanationModal
    }));

  toggleAverage = () =>
    this.setState(({ showAverage }) => ({ showAverage: !showAverage }));

  toggleShowPointPrompt = () => this.setState({ showPointPrompt: false });

  getUserRatingFromPropsOrLocalStorage = (fetchedRatings = []) => {
    const { user } = this.props;

    let userRating = null;

    if (user) {
      userRating = getUserRating({ ratings: fetchedRatings, user });
    } else {
      userRating = this.getUserRatingFromLocalStorage();
    }

    if (!_.isNil(userRating)) {
      this.setState({ userRating });
    }
  };

  render() {
    const {
      userRating,
      showAverage,
      averageRating,
      cursorFollowerRating,
      showPointPrompt,
      isOpenExplanationModal
    } = this.state;

    const {
      type,
      user,
      push,
      className,
      openModal,
      companyName,
      isLoading
    } = this.props;

    return (
      <div className={classnames(styles.maturityMatrixContainer, className)}>
        <div className={styles.titleRow}>
          <span className={styles.title}>UniScore</span>
          <span
            className={styles.explanationTrigger}
            onClick={this.toggleExplanationModal}>
            What is Uniscore?
          </span>
        </div>
        <div className={styles.maturityMatrixBox}>
          {type === 'investor' && <InvestorStaticTextBoxes />}
          {type === 'vendor' && (
            <VendorStaticTextBoxes userRating={userRating} />
          )}
          {isLoading && (
            <div className={styles.loadingOverlay}>
              <Loader className={styles.loader} />
            </div>
          )}
          {_.isNil(user) &&
            showAverage && (
              <div className={styles.unavailableContent}>
                <div className={styles.lock}>
                  <i className="material-icons">lock</i>
                </div>
                <div className={styles.label}>
                  Want to view how others rated {companyName}? Sign up for free
                </div>
                <Button
                  color="blue"
                  className={styles.unavailableContentButton}
                  onClick={() => openModal({ modalName: MODALS.SIGNUP })}>
                  Sign up for free
                </Button>
                <div className={styles.signInText}>
                  Already have an account?{' '}
                  <span
                    className={styles.link}
                    onClick={() => openModal({ modalName: MODALS.LOGIN })}>
                    Sign in
                  </span>
                </div>
              </div>
            )}
          {_.isNil(userRating) &&
            showPointPrompt && (
              <div className={styles.missingPointPromptWrapper}>
                <div className={styles.missingPointPromptContainer}>
                  <span className={styles.text}>
                    See how others have voted for this company
                  </span>
                  <Button
                    className={styles.button}
                    color="primary"
                    onClick={this.toggleShowPointPrompt}>
                    Vote
                  </Button>
                </div>
              </div>
            )}
          <div className={styles.boxBordersContainer} />

          <div
            ref={element => {
              this.ratingsContainer = element;
            }}
            className={styles.boxRatingsContainer}
            onMouseMove={this.handleMouseMove}
            onClick={this.handleClick}
            onMouseLeave={() => this.setState({ cursorFollowerRating: null })}>
            {!_.isNull(cursorFollowerRating) && (
              <div
                className={classnames(
                  styles.rating,
                  styles.myRating,
                  styles.cursorFollowerRating
                )}
                style={{
                  bottom: this.yToCoords(cursorFollowerRating.adaptation),
                  left: this.xToCoords(cursorFollowerRating.innovation)
                }}
              />
            )}
            {userRating && (
              <React.Fragment>
                {showAverage &&
                  !_.isNil(averageRating) &&
                  !_.isNil(user) && (
                    <div
                      className={classnames(styles.rating, styles.average)}
                      style={{
                        bottom: this.yToCoords(averageRating.adaptation),
                        left: this.xToCoords(averageRating.innovation)
                      }}
                    />
                  )}

                <div
                  className={classnames(styles.rating, styles.myRating)}
                  style={{
                    bottom: this.yToCoords(userRating.adaptation),
                    left: this.xToCoords(userRating.innovation)
                  }}
                />
              </React.Fragment>
            )}
          </div>
        </div>
        {userRating && (
          <div className={styles.toggle}>
            <span>View Average</span>
            <ToggleBox
              name="showAverage"
              checked={showAverage}
              onChange={this.toggleAverage}
            />
          </div>
        )}
        <Button
          className={styles.analyticsButton}
          color="primary"
          onClick={() => {
            _.isNil(user)
              ? openModal({ modalName: MODALS.SIGNUP })
              : push(ANALYTICS_URL);
          }}>
          Data Explorer
        </Button>
        {isOpenExplanationModal && (
          <MaturityMatrixExplanationModal
            toggleExplanationModal={this.toggleExplanationModal}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { isLoading, ratings } = state.shared.maturityMatrix;

  return {
    ratings,
    isLoading
  };
};

const mapDispatchToProps = {
  openModal,
  fetchRatings,
  updateRating,
  linkAnonymousRatingsToUser
};

export default WithPush(
  WithUser(
    withRouter(
      connect(
        mapStateToProps,
        mapDispatchToProps
      )(MaturityMatrix)
    )
  )
);
